import { SolicitorSuggestion, SolicitorOfficeSuggestion } from "./types";
import { FontStyles } from "../../../utils/font-styles";
import { Checkbox } from "../../../components/inputs/checkbox";
import "../../../../assets/fa-icons/css/fontawesome.css";
import "../../../../assets/fa-icons/css/sharp-regular.css";

interface SolicitorListItemProps {
  solicitor: SolicitorSuggestion | SolicitorOfficeSuggestion;
  isSelected: boolean;
  onToggle: () => void;
}

// Type guard to check if solicitor has address
function hasAddress(
  solicitor: SolicitorSuggestion | SolicitorOfficeSuggestion,
): solicitor is SolicitorOfficeSuggestion {
  return "address" in solicitor;
}

export const SolicitorListItem = ({ solicitor, isSelected, onToggle }: SolicitorListItemProps) => {
  const handleClick = () => {
    onToggle();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`w-full flex gap-3 md:gap-4 p-3 md:p-4.5 rounded-md border transition-colors cursor-pointer ${
        isSelected ? "border-loch bg-neutral-white" : "border-foggy-firth bg-neutral-white hover:border-neutral-300"
      }`}
    >
      {/* Container 1: Checkbox */}
      <div className="flex items-start md:items-center shrink-0">
        <Checkbox
          id={`solicitor-${solicitor.firmId.toString()}`}
          checked={isSelected}
          className="pointer-events-none"
        />
      </div>

      {/* Container 2: Content wrapper - vertical on mobile, horizontal on desktop */}
      <div className="flex-1 flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
        {/* Container 2a: Name and address */}
        <div className="flex-1 flex flex-col gap-2 items-start text-left">
          <p className={`${FontStyles["body-l"]} font-medium text-slate leading-snug md:leading-none`}>
            {solicitor.name}
          </p>
          {hasAddress(solicitor) && (
            <p className={`${FontStyles["body-md"]} text-light-slate leading-snug md:leading-none`}>
              {solicitor.address}
            </p>
          )}
        </div>

        {/* Container 2b: Charter badge and logo
            Mobile: Full width with space-between - logo (left) and charter badge (right)
            Desktop: Fixed gap with REVERSED order - charter badge (left) and logo (right) */}
        <div className="flex justify-between md:gap-4 md:flex-row-reverse items-center w-full md:w-auto md:shrink-0">
          {/* Logo - maintains aspect ratio, reserves space for alignment */}
          <div className="h-[34px] md:h-[40px] w-auto md:w-[110px] flex items-center justify-center">
            {solicitor.logoUrl && (
              <img
                src={solicitor.logoUrl}
                alt={`${solicitor.name} logo`}
                className="max-h-full max-w-full object-contain"
              />
            )}
          </div>

          {/* ESPC Charter Badge - reserves space on desktop (md+) for alignment */}
          <div className="md:w-[66px] shrink-0">
            {solicitor.isCharterMember && (
              <img
                src="/static/logos/espc-chartered-logo.svg"
                alt="ESPC Charter Member"
                className="h-[34px] md:h-[40px] w-auto"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
